import { FEEDBACK_MAX_TEXT } from "./feedback.utils"

// ── Quando l'app si inceppa ──
// Un errore mentre React disegna, senza niente intorno, lascia la pagina
// bianca. `CrashGuard` (src/crash/CrashGuard.js) lo prende e mostra una
// schermata al suo posto, e da qui parte la segnalazione: un documento in
// `feedback`, come "Segnala un problema", con l'errore in `context.crash`.
// Le regole non cambiano: la gravità è "blocking" (l'app si è fermata), e il
// contesto resta sotto i venti campi che le regole permettono.

// Quanto si tiene dell'errore: abbastanza per trovare il punto, non tanto
// da fare un documento enorme
export const CRASH_MESSAGE_MAX = 300
export const CRASH_STACK_MAX = 1500

// La stessa cosa rotta di nuovo, appena riaperta l'app, non manda un'altra
// segnalazione uguale: una ogni tanto basta
export const CRASH_REPEAT_WINDOW_MS = 10 * 60 * 1000
const LAST_CRASH_KEY = "incampo.lastCrash"

const clip = (text, max) => (text ? String(text).slice(0, max) : null)

// L'errore com'è utile leggerlo: il messaggio, dove si è rotto nel codice
// e dove nei componenti
export const crashDetails = (error, componentStack) => ({
	name: clip(error?.name, 80),
	message: clip(error?.message ?? String(error), CRASH_MESSAGE_MAX),
	stack: clip(error?.stack, CRASH_STACK_MAX),
	componentStack: clip(componentStack?.trim(), CRASH_STACK_MAX),
})

// La segnalazione dell'errore: il testo dice cosa è successo, il resto sta
// nel contesto, accanto a quello che allega anche "Segnala un problema"
export const crashFeedback = (crash, context) => ({
	text: `L'app si è fermata: ${crash.message ?? "errore senza messaggio"}`.slice(
		0,
		FEEDBACK_MAX_TEXT,
	),
	severity: "blocking",
	context: { ...context, crash },
	status: "new",
})

// La riga che il maestro scrive sotto, se la scrive: una segnalazione a
// parte, che dice di quale errore parla (una segnalazione non si cambia,
// nemmeno da chi l'ha scritta)
export const crashNoteFeedback = (note, crashID, context) => ({
	text: note.trim().slice(0, FEEDBACK_MAX_TEXT),
	severity: "blocking",
	context: { ...context, crashID },
	status: "new",
})

// Lo stesso errore nella stessa pagina è la stessa cosa rotta
export const crashKey = (crash, page) => `${page ?? ""}|${crash.message ?? ""}`

// Se mandarla: no se la stessa è partita da poco da questo telefono. Il
// telefono se lo ricorda in `localStorage`; se non si può, la si manda.
export const shouldReportCrash = (key, now = Date.now(), storage = safeStorage()) => {
	try {
		const last = JSON.parse(storage?.getItem(LAST_CRASH_KEY) ?? "null")
		if (last?.key === key && now - last.at < CRASH_REPEAT_WINDOW_MS) return false
		storage?.setItem(LAST_CRASH_KEY, JSON.stringify({ key, at: now }))
	} catch {
		// niente memoria: meglio una segnalazione in più che una in meno
	}
	return true
}

const safeStorage = () => {
	try {
		return window.localStorage
	} catch {
		return null
	}
}
